import api from './api';

export const adminService = {
  // Forms
  getAllForms: async () => {
    const response = await api.get('/admin/forms');
    return response.data;
  },

  renameForm: async (formId, newName) => {
    const response = await api.put(`/admin/forms/${formId}/rename`, { newName });
    return response.data;
  },

  deleteForm: async (formId) => {
    const response = await api.delete(`/admin/forms/${formId}`);
    return response.data;
  },

  // Trash
  getTrash: async () => {
    const response = await api.get('/admin/forms/trash');
    return response.data;
  },

  restoreForm: async (formId) => {
    const response = await api.put(`/admin/forms/${formId}/restore`);
    return response.data;
  },

  permanentDeleteForm: async (formId) => {
    const response = await api.delete(`/admin/forms/${formId}/permanent`);
    return response.data;
  },

  setFormType: async (formId, isInternal) => {
    const response = await api.put(`/admin/forms/${formId}/type`, { isInternal });
    return response.data;
  },

  setFormStatus: async (formId, status) => {
    const response = await api.put(`/admin/forms/${formId}/status`, { status });
    return response.data;
  },

  moveForm: async (formId, clinicId, folderId) => {
    const response = await api.put(`/admin/forms/${formId}/move`, { clinicId, folderId });
    return response.data;
  },

  duplicateForm: async (formId) => {
    const response = await api.post(`/admin/forms/${formId}/duplicate`);
    return response.data;
  },

  getFormPreview: async (formId) => {
    return `/api/admin/forms/${formId}/preview`;
  },

  // Import
  importPdf: async (file, isInternal, useAI = false) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const endpoint = `/admin/forms/upload?smartImport=${useAI}`;
    const response = await api.post(endpoint, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    
    // Set form type after upload
    if (response.data.formId) {
      try {
        await api.put(`/admin/forms/${response.data.formId}/type`, { isInternal });
      } catch (error) {
        console.error('Failed to set form type:', error);
      }
    }
    
    return response.data;
  },

  // Users
  getAllUsers: async () => {
    const response = await api.get('/users');
    return response.data;
  },

  createUser: async (userData) => {
    const response = await api.post('/users', userData);
    return response.data;
  },

  updateUser: async (userId, userData) => {
    const response = await api.put(`/users/${userId}`, userData);
    return response.data;
  },

  deleteUser: async (userId) => {
    const response = await api.delete(`/users/${userId}`);
    return response.data;
  },

  reset2FA: async (userId) => {
    const response = await api.post(`/users/${userId}/reset-2fa`);
    return response.data;
  },

  // Clinics
  getAllClinics: async () => {
    const response = await api.get('/clinics');
    return response.data;
  },

  createClinic: async (clinicData) => {
    const response = await api.post('/clinics', clinicData);
    return response.data;
  },

  updateClinic: async (clinicId, clinicData) => {
    const response = await api.put(`/clinics/${clinicId}`, clinicData);
    return response.data;
  },

  deleteClinic: async (clinicId) => {
    const response = await api.delete(`/clinics/${clinicId}`);
    return response.data;
  },

  getClinicAssignedForms: async (clinicId) => {
    const response = await api.get(`/clinics/${clinicId}/assigned-forms`);
    return response.data;
  },

  // Every clinic's assigned form IDs in one call, as { clinicId: [formId, ...] }.
  // Preferred over calling getClinicAssignedForms per clinic, which would issue
  // one request per clinic and can trip the API rate limit.
  getAllClinicAssignments: async () => {
    const response = await api.get('/clinics/assignments');
    return response.data;
  },

  // Doctors
  getAllDoctors: async () => {
    const response = await api.get('/doctors');
    return response.data;
  },

  createDoctor: async (doctorData) => {
    const response = await api.post('/doctors', doctorData);
    return response.data;
  },

  updateDoctor: async (doctorId, doctorData) => {
    const response = await api.put(`/doctors/${doctorId}`, doctorData);
    return response.data;
  },

  deleteDoctor: async (doctorId) => {
    const response = await api.delete(`/doctors/${doctorId}`);
    return response.data;
  },

  // Folders
  getAllFolders: async () => {
    const response = await api.get('/folders');
    return response.data;
  },

  createFolder: async (folderData) => {
    const response = await api.post('/folders', folderData);
    return response.data;
  },

  updateFolder: async (folderId, folderData) => {
    const response = await api.put(`/folders/${folderId}`, folderData);
    return response.data;
  },

  deleteFolder: async (folderId) => {
    const response = await api.delete(`/folders/${folderId}`);
    return response.data;
  },

  reorderFolders: async (clinicId, folderIds) => {
    const response = await api.put('/folders/reorder', {
      clinicId,
      folderIds
    });
    return response.data;
  },

  reorderClinics: async (clinicIds) => {
    const response = await api.put('/clinics/reorder', {
      clinicIds
    });
    return response.data;
  },

  // Links
  getAllLinks: async (formId) => {
    const params = formId ? { formId } : {};
    const response = await api.get('/formlinks', { params });
    return response.data;
  },

  createLink: async (data) => {
    // data: { formId, patientName?, notes?, expirationHours?, reusable?, doctorEmail?, ccEmail?, bccEmail? }
    const response = await api.post('/formlinks', data);
    return response.data;
  },

  deleteLink: async (token) => {
    const response = await api.delete(`/formlinks/${token}`);
    return response.data;
  },

  // Submissions
  getAllSubmissions: async () => {
    const response = await api.get('/forms/all-submissions');
    return response.data;
  },

  deleteSubmission: async (formId, filename) => {
    const response = await api.delete(`/forms/${formId}/submissions/${encodeURIComponent(filename)}`);
    return response.data;
  },

  deleteDraftSubmission: async (draftToken) => {
    const response = await api.delete(`/forms/submissions/draft/${encodeURIComponent(draftToken)}`);
    return response.data;
  },

  getSubmissionPreviewUrl: (formId, filename) => {
    return `/api/forms/${formId}/submissions/${encodeURIComponent(filename)}?preview=true`;
  },

  getSubmissionDownloadUrl: (formId, filename) => {
    return `/api/forms/${formId}/submissions/${encodeURIComponent(filename)}`;
  },

  // Maintenance
  getMaintenanceSchedule: async () => {
    const response = await api.get('/admin/maintenance');
    return response.data;
  },

  setMaintenanceSchedule: async (data) => {
    const response = await api.post('/admin/maintenance', data);
    return response.data;
  },

  cancelMaintenance: async () => {
    const response = await api.delete('/admin/maintenance');
    return response.data;
  },

  // Consent Links
  createConsentLink: async (data) => {
    const response = await api.post('/consent-links', data);
    return response.data;
  },

  getAllConsentLinks: async () => {
    const response = await api.get('/consent-links');
    return response.data;
  },

  deleteConsentLink: async (token) => {
    const response = await api.delete(`/consent-links/${token}`);
    return response.data;
  },

  /**
   * Verifies the My Submissions access code. The code itself never reaches the
   * browser - it is compared server-side and only pass/fail comes back.
   */
  verifySubmissionsCode: async (code) => {
    const response = await api.post('/security/verify-submissions-code', { code });
    return response.data;
  }
};

export default adminService;
